import { Position } from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import * as React from 'react';

import { TEST_IDS } from 'src/constants/testIds';
import { NodeCategory } from 'src/containers/ProjectDiagram//constants/categories';
import { ClassContextMenu } from 'src/containers/ProjectDiagram/components/contextMenu/ClassContextMenu';
import { HierarchyContextMenu } from 'src/containers/ProjectDiagram/components/contextMenu/HierarchyContextMenu';
import { MappingContextMenu } from 'src/containers/ProjectDiagram/components/contextMenu/MappingContextMenu';
import { MultiSelectionContextMenu } from 'src/containers/ProjectDiagram/components/contextMenu/MultiSelectionContextMenu';
import { RelationshipContextMenu } from 'src/containers/ProjectDiagram/components/contextMenu/RelationshipContextMenu';
import { ResourceContextMenu } from 'src/containers/ProjectDiagram/components/contextMenu/ResourceContextMenu';
import {
  LinkCategory,
  LinkLabelCategory,
} from 'src/containers/ProjectDiagram/constants/categories';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ContextMenuData } from 'src/containers/ProjectDiagram/types/contextMenu';
import {
  MappingLabelNode,
  MappingLink,
  ModelConceptClassNode,
  ModelConceptHierarchyLabelNode,
  ModelConceptHierarchyLink,
  ModelConceptRelationshipLabelNode,
  ModelConceptRelationshipLink,
  ResourceNode,
} from 'src/containers/ProjectDiagram/types/diagram';
import {
  isLink,
  isLinkLabel,
  isNode,
} from 'src/containers/ProjectDiagram/utils/graphObject';
import { hasMultiselection } from 'src/containers/ProjectDiagram/utils/hasMultiselection';

type ProjectDiagramContextMenuProps = {
  contextMenu: ContextMenuData | null;
  onClose: () => void;
};

export const ProjectDiagramContextMenu: React.VFC<
  ProjectDiagramContextMenuProps
> = ({ contextMenu, onClose }) => {
  const diagram = useDiagram();
  const { obj: clickedObj, left, top } = contextMenu || {};

  if (
    !clickedObj ||
    !clickedObj.key ||
    !contextMenu ||
    !diagram ||
    diagram.headless
  ) {
    return null;
  }

  let content: JSX.Element | null = null;
  if (hasMultiselection(diagram)) {
    content = <MultiSelectionContextMenu />;
  } else if (
    isNode<ModelConceptClassNode>(clickedObj, NodeCategory.MODEL_CONCEPT_CLASS)
  ) {
    content = <ClassContextMenu node={clickedObj} />;
  } else if (isNode<ResourceNode>(clickedObj, NodeCategory.RESOURCE)) {
    content = <ResourceContextMenu node={clickedObj} />;
  } else if (
    isLinkLabel<ModelConceptRelationshipLabelNode>(
      clickedObj,
      LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL
    ) &&
    clickedObj.labeledLink
  ) {
    content = <RelationshipContextMenu link={clickedObj.labeledLink} />;
  } else if (
    isLink<ModelConceptRelationshipLink>(
      clickedObj,
      LinkCategory.MODEL_CONCEPT_RELATIONSHIP
    )
  ) {
    content = <RelationshipContextMenu link={clickedObj} />;
  } else if (
    isLinkLabel<ModelConceptHierarchyLabelNode>(
      clickedObj,
      LinkLabelCategory.MODEL_CONCEPT_HIERARCHY_LABEL
    ) &&
    clickedObj.labeledLink
  ) {
    content = <HierarchyContextMenu link={clickedObj.labeledLink} />;
  } else if (
    isLink<ModelConceptHierarchyLink>(
      clickedObj,
      LinkCategory.MODEL_CONCEPT_HIERARCHY
    )
  ) {
    content = <HierarchyContextMenu link={clickedObj} />;
  } else if (
    isLinkLabel<MappingLabelNode>(
      clickedObj,
      LinkLabelCategory.MAPPING_LABEL
    ) &&
    clickedObj.labeledLink
  ) {
    content = <MappingContextMenu link={clickedObj.labeledLink} />;
  } else if (isLink<MappingLink>(clickedObj, LinkCategory.MAPPING)) {
    content = <MappingContextMenu link={clickedObj} />;
  }

  return (
    <div
      data-testid={TEST_IDS.projectDiagram.contextMenu.container}
      style={{
        position: 'absolute',
        left,
        top,
      }}
    >
      <Popover2
        canEscapeKeyClose
        content={
          <div
            className="sd-context-menu"
            onContextMenu={(e) => e.preventDefault()}
          >
            {content}
          </div>
        }
        enforceFocus={false}
        // will not close on canvas click, so a backdrop is necessary
        hasBackdrop
        isOpen
        minimal
        onClose={onClose}
        position={Position.RIGHT_TOP}
        targetTagName="div"
      >
        <div />
      </Popover2>
    </div>
  );
};
